/**
 * Live-streaming state for the meeting screen.
 *
 * This store used to be the whole feature: `start()` set `isLive` and the UI
 * showed a live badge, a rising viewer count and a health indicator over a
 * stream that did not exist. The stream key the host typed was collected and
 * dropped. It now drives the real thing.
 *
 * Two things are gone rather than reimplemented:
 *
 *   `viewerCount` was `count + Math.floor(Math.random() * 3)`. A real number
 *   would have to come from the platform's own API (YouTube Data API for a
 *   broadcast we only know by its ingest key), which is a separate integration
 *   with its own OAuth. An invented audience is worse than none.
 *
 *   `health` was a field nothing computed. What we can honestly say is whether
 *   the last heartbeat reached the mixer, so that is what `connection` reports.
 *
 * The heartbeat lives here rather than in a component because the stream must
 * outlive any one panel being open — closing the live-streaming dialog is not
 * the same as ending the broadcast — while still stopping the moment the store
 * says the stream is over.
 */

import { create } from 'zustand'
import {
  heartbeatStream,
  startStream,
  stopStream,
  stopStreamOnUnload,
  type StartStreamInput,
  type StreamPlatform,
} from '../services/liveStream.api'

export type BroadcastPlatform = StreamPlatform

/** Whether the last heartbeat reached the mixer. Not stream quality. */
export type BroadcastConnection = 'live' | 'reconnecting'

type MeetingBroadcastingState = {
  isLive: boolean
  platform: BroadcastPlatform | null
  title: string
  connection: BroadcastConnection
  startedAt: number | null
  now: number
  /** Set when a start or stop fails, so the dialog can say why. */
  error: string | null
  isBusy: boolean

  setNow: (n: number) => void
  start: (meetingId: string, input: StartStreamInput & { title?: string }) => Promise<boolean>
  stop: (meetingId: string) => Promise<void>
  /** Called from a `pagehide` listener — best effort, never awaited. */
  endOnUnload: (meetingId: string) => void
}

/**
 * The heartbeat timer, module-level rather than in the store.
 *
 * It is not state anything renders, and putting a timer id in the store would
 * make every tick a state change for subscribers that only care whether the
 * stream is live.
 */
let heartbeatTimer: ReturnType<typeof setInterval> | null = null

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

export const useMeetingBroadcastingStore = create<MeetingBroadcastingState>((set, get) => ({
  isLive: false,
  platform: null,
  title: '',
  connection: 'live',
  startedAt: null,
  now: Date.now(),
  error: null,
  isBusy: false,

  setNow: (now) => set({ now }),

  start: async (meetingId, input) => {
    if (get().isLive || get().isBusy) return false
    set({ isBusy: true, error: null })

    try {
      const res = await startStream(meetingId, input)
      const ts = res.startedAt || Date.now()

      set({
        isLive: true,
        platform: input.platform,
        title: input.title ?? '',
        connection: 'live',
        startedAt: ts,
        now: ts,
        isBusy: false,
        error: null,
      })

      // The server decides the cadence from the lease it is holding.
      const everyMs = Math.max(5, res.heartbeatSeconds || 10) * 1000
      stopHeartbeat()
      heartbeatTimer = setInterval(() => {
        void heartbeatStream(meetingId)
          .then(({ streaming, message }) => {
            if (streaming) {
              set({ connection: 'live' })
              return
            }
            // The lease lapsed, someone stopped it, or the platform refused the
            // key. Reflect that rather than keep showing a live badge over a
            // stream that has ended — and when there is a reason, say it, since
            // a host whose key was rejected can only fix what they are told.
            stopHeartbeat()
            set({
              isLive: false,
              startedAt: null,
              connection: 'live',
              error: message ?? null,
            })
          })
          .catch(() => {
            // One missed heartbeat is not the end — the lease allows for a few.
            // Say "reconnecting" and let the next one settle it either way.
            set({ connection: 'reconnecting' })
          })
      }, everyMs)

      return true
    } catch (err) {
      set({
        isBusy: false,
        isLive: false,
        error: err instanceof Error ? err.message : 'Could not start the live stream',
      })
      return false
    }
  },

  stop: async (meetingId) => {
    stopHeartbeat()
    // Clear the UI first: the host asked to stop, and the stream is ending
    // whatever the response says — if the call fails, the lease finishes it.
    set({ isLive: false, startedAt: null, isBusy: true })
    try {
      await stopStream(meetingId)
      set({ isBusy: false, error: null })
    } catch (err) {
      set({
        isBusy: false,
        error: err instanceof Error ? err.message : 'Could not stop the live stream',
      })
    }
  },

  endOnUnload: (meetingId) => {
    if (!get().isLive) return
    stopHeartbeat()
    stopStreamOnUnload(meetingId)
  },
}))

export const selectBroadcastDurationSeconds = (s: MeetingBroadcastingState): number => {
  if (!s.isLive || !s.startedAt) return 0
  return Math.max(0, Math.floor((s.now - s.startedAt) / 1000))
}

export const selectBroadcastPlatformLabel = (s: MeetingBroadcastingState): string => {
  if (!s.platform) return ''
  if (s.platform === 'youtube') return 'YouTube'
  if (s.platform === 'facebook') return 'Facebook'
  return 'Custom'
}
